import React, { useEffect, useMemo, useState } from 'react';
import CircularProgress from '@/components/common/CircularProgress';
import { Button } from '@/components/ui/button';
import {
  fetchPsiPlusHistorialDetailBe,
  PsiPlusHistorialDetailBe,
  PsiPlusHistorialSessionBe,
  PsiPlusQuarterResultBe,
  PsiPlusSinBonoBe,
  PsiPlusTier,
} from '@/hooks/api/psiPlus';
import { explainSinBono, formatPatientNames, formatQuarterLabel, motivacionSinBono } from '@/utils/psiPlusHistorial';

interface Props {
  trimestres: PsiPlusQuarterResultBe[];
  sinBono: PsiPlusSinBonoBe[];
  /** Emoji y monto de cada tramo (los define MetricsPsiPlusTab). */
  tierDisplay: Record<PsiPlusTier, { emoji: string; bonus: string }>;
  formatDate: (iso: string) => string;
}

type Item =
  | { kind: 'paid'; data: PsiPlusQuarterResultBe }
  | { kind: 'none'; data: PsiPlusSinBonoBe };

function SessionsTable({ sessions, formatDate }: { sessions: PsiPlusHistorialSessionBe[]; formatDate: (iso: string) => string }) {
  return (
    <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-left text-xs" data-testid="historial-sesiones-tabla">
        <thead className="sticky top-0 bg-gray-50 text-gray-500">
          <tr>
            <th className="px-3 py-2 font-semibold">Fecha</th>
            <th className="px-3 py-2 font-semibold">Proceso</th>
            <th className="px-3 py-2 font-semibold">Paciente(s)</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s, i) => (
            <tr key={`${s.date}-${i}`} className="border-t border-gray-100 text-gray-700">
              <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(s.date)}</td>
              <td className="px-3 py-1.5">{s.processType ?? '—'}</td>
              <td className="px-3 py-1.5">{formatPatientNames(s.patientNames)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Detail({ item, formatDate }: { item: Item; formatDate: (iso: string) => string }) {
  const [detail, setDetail] = useState<PsiPlusHistorialDetailBe | null>(null);
  const [failed, setFailed] = useState(false);
  const { quarterYear, quarter } = item.data;

  useEffect(() => {
    let cancelled = false;
    fetchPsiPlusHistorialDetailBe(quarterYear, quarter)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [quarterYear, quarter]);

  if (failed) {
    return <p className="text-xs text-gray-500">No pudimos cargar el detalle. Probá de nuevo en unos minutos.</p>;
  }
  if (!detail) {
    return <div className="flex justify-center py-4"><CircularProgress /></div>;
  }

  if (detail.outcome === 'paid') {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-gray-600">
          Estas son las {detail.sessions.length} sesiones ponderadas (universo {detail.winningUniverse}+) con las que
          alcanzaste el bono. Es la foto que se verificó al cerrar el trimestre.
        </p>
        <SessionsTable sessions={detail.sessions} formatDate={formatDate} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-gray-600">
        Una sesión es ponderada desde la 5ª sesión de un proceso (universo 5+) o desde la 10ª (universo 10+).
        Para el primer bono se necesitan 150 sesiones ponderadas del universo 5+.
      </p>
      <div className="max-h-96 overflow-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-left text-xs" data-testid="historial-procesos-tabla">
          <thead className="sticky top-0 bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 font-semibold">Paciente(s)</th>
              <th className="px-3 py-2 font-semibold">Proceso</th>
              <th className="px-3 py-2 font-semibold text-right">Sesiones acumuladas</th>
              <th className="px-3 py-2 font-semibold text-right">En el trimestre</th>
              <th className="px-3 py-2 font-semibold text-right">Ponderadas 5+</th>
              <th className="px-3 py-2 font-semibold text-right">Ponderadas 10+</th>
            </tr>
          </thead>
          <tbody>
            {detail.processes.map((p, i) => (
              <tr key={i} className="border-t border-gray-100 text-gray-700">
                <td className="px-3 py-1.5">{formatPatientNames(p.patientNames)}</td>
                <td className="px-3 py-1.5">{p.processType ?? '—'}</td>
                <td className="px-3 py-1.5 text-right">{p.totalSessions}</td>
                <td className="px-3 py-1.5 text-right">{p.sessionsInQuarter}</td>
                <td className="px-3 py-1.5 text-right">{p.eligible5InQ}</td>
                <td className="px-3 py-1.5 text-right">{p.eligible10InQ}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details className="text-xs text-gray-600">
        <summary className="cursor-pointer font-semibold">Sesiones ponderadas del universo 5+ ({detail.sessions.length})</summary>
        <div className="mt-2">
          <SessionsTable sessions={detail.sessions} formatDate={formatDate} />
        </div>
      </details>
    </div>
  );
}

/**
 * Lista del sub-tab "Historial" de PSI+: trimestres cobrados (verde) y trimestres cerrados en los
 * que no se llegó a ningún tramo (gris, "Sin bono"). Cada uno se expande y carga su detalle recién
 * al abrirlo (GET /psychologist/psi-plus/historial/:año/:trimestre).
 */
export const PsiPlusHistorialList: React.FC<Props> = ({ trimestres, sinBono, tierDisplay, formatDate }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);

  const items = useMemo<Item[]>(() => {
    const all: Item[] = [
      ...trimestres.map((data): Item => ({ kind: 'paid', data })),
      ...sinBono.map((data): Item => ({ kind: 'none', data })),
    ];
    return all.sort((a, b) => b.data.quarterYear - a.data.quarterYear || b.data.quarter - a.data.quarter);
  }, [trimestres, sinBono]);

  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => {
        const key = `${item.data.quarterYear}-${item.data.quarter}`;
        const isOpen = openKey === key;
        const label = formatQuarterLabel(item.data.quarterYear, item.data.quarter);
        const toggle = (
          <Button
            variant="outline"
            size="sm"
            className="self-start mt-2"
            aria-expanded={isOpen}
            onClick={() => setOpenKey(isOpen ? null : key)}
            data-testid={`historial-toggle-${key}`}
          >
            {isOpen
              ? 'Ocultar detalle'
              : item.kind === 'paid'
                ? 'Ver las sesiones del bono'
                : 'Ver por qué no llegué'}
          </Button>
        );

        if (item.kind === 'paid') {
          const t = item.data;
          return (
            <div key={key} data-testid={`historial-cobrado-${key}`} className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-5 py-4 flex flex-col">
              <div className="flex items-center gap-4">
                <span className="text-3xl shrink-0">{tierDisplay[t.tierLabel].emoji}</span>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-600">{label}</p>
                  <p className="text-lg font-extrabold text-emerald-700">{tierDisplay[t.tierLabel].bonus} cobrados</p>
                  <p className="text-xs text-gray-500">
                    Pagado el {formatDate(t.paidAt)}
                    {t.currency && t.currency.toLowerCase() !== 'usd' && (
                      <> · {t.currency.toUpperCase()} {t.montoEnMonedaDePago.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
                    )}
                  </p>
                </div>
              </div>
              {toggle}
              {isOpen && <div className="mt-3"><Detail item={item} formatDate={formatDate} /></div>}
            </div>
          );
        }

        const s = item.data;
        return (
          <div key={key} data-testid={`historial-sin-bono-${key}`} className="rounded-2xl border-2 border-gray-200 bg-gray-50 px-5 py-4 flex flex-col">
            <div className="flex items-center gap-4">
              <span className="text-3xl shrink-0" role="img" aria-label="Meta del trimestre">🎯</span>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</p>
                <p className="text-lg font-extrabold text-gray-700">Sin bono en este trimestre</p>
                <p className="text-xs text-gray-600">No se abonó bono porque no se alcanzó el mínimo de sesiones. {explainSinBono(s)}</p>
                <p className="text-xs font-semibold text-primary mt-1" data-testid="historial-motivacion">{motivacionSinBono(s)}</p>
              </div>
            </div>
            {toggle}
            {isOpen && <div className="mt-3"><Detail item={item} formatDate={formatDate} /></div>}
          </div>
        );
      })}
    </div>
  );
};
